import { createContext, useContext, useState, useEffect } from "react";
import { onSnapshot, doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

// Night slots: 18:00–23:00 and 00:00–05:00 (midnight wrap)
export function isNightSlot(slotTime) {
  const h = parseInt(slotTime, 10);
  return h >= 18 || h < 6;
}

// ── Defaults (used before Firestore loads and as fallback) ────────────────────
export const PRICING_DEFAULTS = {
  "Oval Cricket Ground": { weekdayDay: 800,  weekdayNight: 1100, weekendDay: 1200,  weekendNight: 1500 },
  "Box Cricket Court 1": { weekdayDay: 500,  weekdayNight: 800,  weekendDay: 750,   weekendNight: 1050 },
  "Box Cricket Court 2": { weekdayDay: 500,  weekdayNight: 800,  weekendDay: 750,   weekendNight: 1050 },
  "Pickleball Court 1":  { weekdayDay: 300,  weekdayNight: 600,  weekendDay: 450,   weekendNight: 750  },
  "Pickleball Court 2":  { weekdayDay: 300,  weekdayNight: 600,  weekendDay: 450,   weekendNight: 750  },
};

// Detect old structure (has weekday/weekend but missing new 4-field keys)
function needsMigration(courtData) {
  return (
    courtData &&
    ("weekday" in courtData || "weekend" in courtData) &&
    !("weekdayDay" in courtData)
  );
}

// Convert a single court's old {weekday, weekend} → new 4-field structure
function migrateCourtPrices(old) {
  const wd = Number(old.weekday) || 0;
  const we = Number(old.weekend) || 0;
  return {
    weekdayDay:   wd,
    weekdayNight: wd + 300,
    weekendDay:   we,
    weekendNight: we + 300,
  };
}

// Safe number read — returns 0 instead of NaN for missing/undefined values
function safeNum(v, fallback = 0) {
  const n = Number(v);
  return isFinite(n) ? n : fallback;
}

// Maps sport name (as used in booking) → representative court key
export const SPORT_COURT_MAP = {
  "Oval Cricket": "Oval Cricket Ground",
  "Box Cricket":  "Box Cricket Court 1",
  "Pickleball":   "Pickleball Court 1",
};

export function isWeekend(dateStr) {
  const d = new Date(dateStr);
  return d.getDay() === 0 || d.getDay() === 6;
}

// Format a number as "₹X,XXX"
export function fmtPrice(n) {
  return "₹" + Number(n).toLocaleString();
}

// ── Context ───────────────────────────────────────────────────────────────────
const PricingContext = createContext(null);

export function PricingProvider({ children }) {
  const [pricing, setPricing] = useState(PRICING_DEFAULTS);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "pricing"), async (snap) => {
      if (!snap.exists()) return;

      const raw = snap.data();

      // Check whether any court still uses old structure
      const anyCourtsOld = Object.values(raw).some(
        (v) => v && typeof v === "object" && needsMigration(v)
      );

      if (anyCourtsOld) {
        // Build migrated document — upgrade old courts, leave already-migrated ones alone
        const migrated = {};
        for (const [court, data] of Object.entries(raw)) {
          if (data && typeof data === "object") {
            migrated[court] = needsMigration(data) ? migrateCourtPrices(data) : data;
          }
        }
        // Fill in any courts that were missing from Firestore entirely
        for (const [court, defaults] of Object.entries(PRICING_DEFAULTS)) {
          if (!migrated[court]) migrated[court] = defaults;
        }
        // Write back once — next onSnapshot will fire with the new data
        try {
          await setDoc(doc(db, "settings", "pricing"), migrated);
        } catch (e) {
          console.error("[PricingContext] Migration write failed:", e);
          // Still apply locally so UI isn't broken
          setPricing((prev) => ({ ...prev, ...migrated }));
        }
        return; // wait for the next snapshot (which will have the migrated data)
      }

      // Normal path — document already has new structure
      // Ensure no fields are undefined/NaN by merging with defaults
      const normalised = {};
      for (const [court, data] of Object.entries(raw)) {
        if (data && typeof data === "object") {
          const def = PRICING_DEFAULTS[court] || PRICING_DEFAULTS["Oval Cricket Ground"];
          normalised[court] = {
            weekdayDay:   safeNum(data.weekdayDay,   def.weekdayDay),
            weekdayNight: safeNum(data.weekdayNight, def.weekdayNight),
            weekendDay:   safeNum(data.weekendDay,   def.weekendDay),
            weekendNight: safeNum(data.weekendNight, def.weekendNight),
          };
        }
      }
      setPricing((prev) => ({ ...prev, ...normalised }));
    });
    return () => unsub();
  }, []);

  // Price for a specific court + slot time + date — never returns NaN
  const getSlotPrice = (courtName, slotTime, dateStr) => {
    const c   = pricing[courtName] || PRICING_DEFAULTS[courtName];
    const def = PRICING_DEFAULTS[courtName] || PRICING_DEFAULTS["Oval Cricket Ground"];
    const night = isNightSlot(slotTime);
    const wknd  = isWeekend(dateStr);
    if (!c) return wknd ? (night ? def.weekendNight : def.weekendDay) : (night ? def.weekdayNight : def.weekdayDay);
    if (wknd) return safeNum(night ? c.weekendNight : c.weekendDay, night ? def.weekendNight : def.weekendDay);
    return safeNum(night ? c.weekdayNight : c.weekdayDay, night ? def.weekdayNight : def.weekdayDay);
  };

  // Day rate for a court on a date (used for display / promo reference)
  const getCourtRate = (courtName, dateStr) => {
    const c   = pricing[courtName] || PRICING_DEFAULTS[courtName];
    const def = PRICING_DEFAULTS[courtName] || PRICING_DEFAULTS["Oval Cricket Ground"];
    if (!c) return isWeekend(dateStr) ? def.weekendDay : def.weekdayDay;
    return isWeekend(dateStr)
      ? safeNum(c.weekendDay, def.weekendDay)
      : safeNum(c.weekdayDay, def.weekdayDay);
  };

  // Day rate for a sport name on a date (representative court)
  const getSportRate = (dateStr, sportName) => {
    const courtKey = SPORT_COURT_MAP[sportName];
    if (!courtKey) return isWeekend(dateStr) ? 1200 : 800;
    return getCourtRate(courtKey, dateStr);
  };

  return (
    <PricingContext.Provider value={{ pricing, getSportRate, getCourtRate, getSlotPrice, fmtPrice }}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing() {
  return useContext(PricingContext);
}
